import { useRef } from "react";
import { useHistory } from "react-router-dom";
import ContentHeader from "../../components/app/headers/ContentHeader";
import CreatePlaylistButton from "../../components/profile/CreatePlaylistButton";
import ItemList from "../../components/app/ItemList";
import PlaylistCard from "../../components/cards/PlaylistCard";
import Loader from "../../components/Loader";
import { useCurrentUser } from "../../domains/user/hooks/useCurrentUser";
import { useApiInfiniteQuery, useApiQuery } from "../../services/api";
import { AnalyticsItemList } from "../../tools/analytics/analytics.types";
import { gaEventSelectItemPlaylist } from "../../tools/analytics/domains/playlist.analytics";
import ScrollableLayout from "../../components/layout/ScrollableLayout";
import { usePageScroll } from "../../domains/app/hooks/usePageScroll";
import { useEffectOnce } from "../../domains/app/hooks/useEffectOnce";

const limit = 10;

// Whose playlists the page lists. The reader's own ones are paginated; another
// user's come from the same unpaginated read the profile page previews.
type PlaylistsSource =
  { kind: "created" } | { kind: "shared" } | { kind: "user"; userId: string };

export default function ProfilePlaylistsListPage({
  source,
}: {
  source: PlaylistsSource;
}) {
  const history = useHistory();
  const { currentUser } = useCurrentUser();
  const scrollRef = useRef<HTMLDivElement>(null);
  const { savePageScrollY, restorePageScrollY } = usePageScroll(scrollRef);
  const createdQuery = useApiInfiniteQuery(
    "/webapp/private/playlists/me",
    source.kind === "created" ? { query: { limit }, durable: true } : false
  );
  const sharedQuery = useApiInfiniteQuery(
    "/webapp/private/playlists/shared-with-me",
    source.kind === "shared" ? { query: { limit }, durable: true } : false
  );
  const userQuery = useApiQuery(
    "/webapp/private/playlists/user/:userId",
    source.kind === "user" ? { param: { userId: source.userId } } : false
  );
  const pagedQuery = source.kind === "shared" ? sharedQuery : createdQuery;
  const displayedPlaylists =
    source.kind === "user"
      ? userQuery.state === "success"
        ? userQuery.body
        : []
      : pagedQuery.state === "success"
        ? pagedQuery.docs
        : [];
  const isLoading =
    source.kind === "user"
      ? userQuery.state === "pending"
      : pagedQuery.state === "pending";
  // The user variant leaves `pagedQuery` disabled (idle), so it never pages.
  const canFetchNextPage =
    pagedQuery.state === "success" && pagedQuery.canFetchNextPage;
  const errorMessage =
    source.kind === "user"
      ? userQuery.state === "error"
        ? userQuery.message
        : null
      : pagedQuery.state === "error"
        ? pagedQuery.message
        : null;
  const fetchNextPage =
    pagedQuery.state === "success" ? pagedQuery.fetchNextPage : () => void 0;

  useEffectOnce(() => {
    if (displayedPlaylists.length > 0) {
      restorePageScrollY();
    } else {
      window.scrollTo(0, 0);
    }
  }, []);

  function handleScrollEnd() {
    if (canFetchNextPage) fetchNextPage();
  }

  return (
    <ScrollableLayout
      scrollRef={scrollRef}
      background="var(--color-ds-white)"
      header={<ContentHeader />}
      onScrollEnd={handleScrollEnd}
    >
      {errorMessage !== null ? (
        <p className="px-5 py-6 text-ui-body text-ds-slate-600">
          {errorMessage}
        </p>
      ) : !currentUser || isLoading ? (
        <Loader />
      ) : (
        <ItemList className="pt-[16px]!">
          {source.kind === "created" && <CreatePlaylistButton />}
          {displayedPlaylists.map((playlist) => (
            <PlaylistCard
              key={playlist.id}
              playlist={playlist}
              variant="light"
              onClick={() => {
                savePageScrollY();
                gaEventSelectItemPlaylist({
                  playlist,
                  listName: AnalyticsItemList.PROFILE,
                });
                history.push({
                  pathname: `/profile/playlist/${playlist.id}`,
                  state: { playlist },
                });
              }}
            />
          ))}
        </ItemList>
      )}
    </ScrollableLayout>
  );
}
